import {
  APISimplifiedAsset,
  APISurveyAttributes,
  ArdoqId,
  Asset,
  AssetPayload,
  AssetType,
  CreateViewpointAttributes,
  UnpersistedEntity,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import { Method, createEntity, fetchJson, toRequestOptions } from './api';
import { isArdoqError } from '@ardoq/common-helpers';
import { fileAssetInFolder } from './fileAssetInFolder';

type GenericAssetResponse = Asset & {
  ardoq: { 'entity-type': AssetType };
};

const deleteAssets = (
  assets: AssetPayload[]
): ApiResponse<GenericAssetResponse[]> => {
  const request = new Request(
    `/api/asset/delete`,
    toRequestOptions(Method.POST, { assets })
  );
  return fetchJson<GenericAssetResponse[]>(request);
};

// Exclude entity types which do not use the base createEntity function
// They should have a custom createInFolder function in the api associated with their domain
type ExcludedEntities = APISurveyAttributes | CreateViewpointAttributes;

const createInFolder = async <T extends Omit<Asset, 'meta'>>(
  asset: UnpersistedEntity<Exclude<T, ExcludedEntities>>,
  folderId: ArdoqId,
  assetType: AssetType
): ApiResponse<T> => {
  const assetResponse = await createEntity<T>(assetType)(asset);
  if (isArdoqError(assetResponse)) {
    return assetResponse;
  }
  await fileAssetInFolder({
    assetId: assetResponse._id,
    folderId,
    assetType,
  });
  return assetResponse;
};

type AssetUsageResponse = Array<{ _id: ArdoqId; type: AssetType }>;

const assetUsage = async ({ type, id }: { type: string; id: ArdoqId }) => {
  const request = new Request(
    `/api/asset/${type}/${id}/usage`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<AssetUsageResponse>(request);
};

const simplifiedList = (): ApiResponse<APISimplifiedAsset[]> => {
  const request = new Request(
    '/api/asset/simplified-list',
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APISimplifiedAsset[]>(request);
};

export const assetApi = {
  deleteAssets,
  createInFolder,
  assetUsage,
  simplifiedList,
};
